import React, {useEffect, useRef, useState} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {useIntl} from 'react-intl';
import {Input, Label} from 'reactstrap';
import Button from 'src/components/button';
import styles from './billing-block.module.scss';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  onConfirm?: () => void;
  cancelLabel?: string;
  onCancel: () => void;
  busy?: boolean;
  confirmDisabled?: boolean;
  acceptLabel?: string;
  danger?: boolean;
  children?: React.ReactNode;
}

// Cancel comes first in the DOM so it is the default focus when the dialog has no field (FR-021e).
const ConfirmDialog = ({
  open, title, description, confirmLabel, onConfirm, onCancel, cancelLabel, busy = false,
  confirmDisabled = false, acceptLabel, danger = false, children,
}: ConfirmDialogProps) => {
  const intl = useIntl();
  const [accepted, setAccepted] = useState(false);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) setAccepted(false);
  }, [open]);

  const blocked = busy || confirmDisabled || (!!acceptLabel && !accepted);

  return (
    <Dialog.Root open={open} onOpenChange={(next) => {
      if (!next && !busy) onCancel();
    }}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.dialog}
          onOpenAutoFocus={() => {
            opener.current = document.activeElement as HTMLElement | null;
          }}
          // Radix only returns focus to a Dialog.Trigger; these dialogs are opened from table rows and forms.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            opener.current?.focus();
          }}
        >
          <Dialog.Title className={styles.dialogTitle}>{title}</Dialog.Title>
          <Dialog.Description asChild><div>{description}</div></Dialog.Description>
          {children}
          {acceptLabel && (
            <div className="form-check">
              <Input
                id="billing-block-accept"
                type="checkbox"
                className="form-check-input"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
              />
              <Label for="billing-block-accept" className="form-check-label">{acceptLabel}</Label>
            </div>
          )}
          <div className={styles.dialogActions}>
            <Button className="neutral" color="secondary" disabled={busy} onClick={onCancel}>
              {cancelLabel || intl.formatMessage({id: 'billingBlockCancel'})}
            </Button>
            {confirmLabel && onConfirm && (
              <Button className={danger ? '' : 'solid'} color={danger ? 'danger' : 'theme'} disabled={blocked} onClick={onConfirm}>
                {confirmLabel}
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ConfirmDialog;
